import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
    IconHome2,
    IconRouter,
    IconBell,
    IconChartAreaLine,
} from "@tabler/icons";
import { Breadcrumbs, Typography } from "@mui/material";
import { lowerCase } from "lodash";
import { useSelector } from "react-redux";
import NavGroup from "./NavGroup";
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import { IconChartHistogram, IconMap2, IconMapSearch, IconClockPlay } from '@tabler/icons-react';
import SupervisorAccountOutlinedIcon from '@mui/icons-material/SupervisorAccountOutlined';
import RuleOutlinedIcon from '@mui/icons-material/RuleOutlined';
import FormatListBulletedOutlinedIcon from '@mui/icons-material/FormatListBulletedOutlined';
const MenuItems = ({ type }) => {
    const { t, i18n } = useTranslation();
    const { user } = useSelector((state) => state.user);
    const [menu, setMenu] = useState(null);

    const gis = useMemo(
        () => ({
            id: "gis",
            title: t("gis"),
            type: "group",
            children: [
                {
                    id: "map-monitoring-points",
                    title: t("map-monitoring-points"),
                    type: "item",
                    url: "/dashboard/global-view/map-monitoring-points",
                    icon: IconMap2,
                    breadcrumbs: false,
                },
                {
                    id: "map-real-time",
                    title: t("map-real-time"),
                    type: "item",
                    url: "/dashboard/global-view/map-realTime",
                    icon: IconMapSearch,
                    breadcrumbs: false,
                },
                {
                    id: "map-animated",
                    title: t("map-animated"),
                    type: "item",
                    url: "/dashboard/global-view/map-animated",
                    icon: IconClockPlay,
                    breadcrumbs: false,
                },
            ],
        }),
        [t]
    );

    const dashboardOpSupUser = useMemo(
        () => ({
            id: "dashboard",
            title: t("dashboard"),
            type: "group",
            children: [
                {
                    id: "home",
                    title: t("home"),
                    type: "item",
                    url: "/dashboard/home",
                    icon: IconHome2,
                    breadcrumbs: false,
                },
                {
                    id: "status-sensors",
                    title: t("status-sensors"),
                    type: "item",
                    url: "/dashboard/status-sensors",
                    icon: RuleOutlinedIcon,
                    breadcrumbs: false,
                },
                {
                    id: "warning-list",
                    title: t("Alarm list"),
                    type: "item",
                    url: "/dashboard/warning-list",
                    icon: IconBell,
                    breadcrumbs: false,
                },
            ],
        }),
        [t]
    );

    const dataSensors = useMemo(
        () => ({
            id: "data-sensors",
            title: (
                <Typography sx={{ textTransform: "none", fontWeight: 500 }}>
                    {t("data-sensors")}
                </Typography>
            ),
            type: "group",
            children: [
                {
                    id: "global",
                    title: t("global"),
                    type: "item",
                    url: "/dashboard/data-sensors/global",
                    icon: IconChartAreaLine,
                    breadcrumbs: false,
                },
                {
                    id: "metrics",
                    title: t("metrics"),
                    type: "item",
                    url: "/dashboard/data-sensors/metrics",
                    icon: IconChartHistogram,
                    breadcrumbs: false,
                },
                {
                    id: "historical-data",
                    title: t("Historical data"),
                    type: "item",
                    url: "/dashboard/data-sensors/historical-data",
                    icon: CalendarMonthIcon,
                    breadcrumbs: false,
                },
            ],
        }),
        [t]
    );

    const management = useMemo(
        () => ({
            id: "management",
            title: t("management"),
            type: "group",
            children: [

                {
                    id: "monitoring-points-list",
                    title: t("monitoring-points"),
                    type: "item",
                    url: "/dashboard/management/monitoring-points-list",
                    icon: FormatListBulletedOutlinedIcon,
                    breadcrumbs: false,
                },
                {
                    id: "users",
                    title: t("users"),
                    type: "item",
                    url: "/dashboard/management/users-list",
                    icon: SupervisorAccountOutlinedIcon,
                    breadcrumbs: false,
                },
            ],
        }),
        [t]
    );

    const dashboard = useMemo(
        () => ({
            id: "dashboard",
            title: t("dashboard"),
            type: "group",
            children: [
                {
                    id: "home",
                    title: t("home"),
                    type: "item",
                    url: "/dashboard/home",
                    icon: IconHome2,
                    breadcrumbs: false,
                },
            ],
        }),
        [t]
    );

    const managementOperator = useMemo(
        () => ({
            id: "management",
            title: t("management"),
            type: "group",
            children: [
                {
                    id: "monitoring-points-list",
                    title: t("monitoring-points"),
                    type: "item",
                    url: "/dashboard/management/monitoring-points-list",
                    icon: IconRouter,
                    breadcrumbs: false,
                },
            ],
        }),
        [t]
    );

    const menuItems = useMemo(
        () => ({
            items: [dashboardOpSupUser, dataSensors, gis, management],
        }),
        [dashboardOpSupUser, dataSensors, gis, management]
    );

    const menuItemsVisitor = useMemo(
        () => ({
            items: [dashboard, dataSensors, gis],
        }),
        [dashboard, dataSensors, gis]
    );

    const menuItemsOperator = useMemo(
        () => ({
            items: [dashboardOpSupUser, dataSensors, gis, managementOperator],
        }),
        [dashboardOpSupUser, dataSensors, gis, managementOperator]
    );

    const navItems =
        menu &&
        menu.items.map((item) => {
            switch (item.type) {
                case "group":
                    return <NavGroup key={item.id} item={item} />;
                default:
                    return (
                        <Typography key={item.id} variant="h6" color="error" align="center">
                            Menu Items Error
                        </Typography>
                    );
            }
        });

    useEffect(() => {
        if (lowerCase(user?.role) === "visitor") {
            setMenu(menuItemsVisitor);
        } else {
            if (lowerCase(user?.role) === "operator") {
                setMenu(menuItemsOperator);
            } else {
                setMenu(menuItems);
            }
        }
    }, [user, i18n.language, menuItemsVisitor, menuItemsOperator, menuItems]);

    return type === "MenuList" ? (
        <>{navItems}</>
    ) : (
        <Breadcrumbs
            //separator={IconChevronRight}
            navigation={
                user && lowerCase(user.role) === "visitor"
                    ? menuItemsVisitor
                    : lowerCase(user?.role) === "operator"
                        ? menuItemsOperator
                        : menuItems
            }
        />
    );
};

export default MenuItems;
